import React, { useState, useEffect, useRef, useCallback } from "react";
import "./BimTab.css";
import { useFinancialPlanState } from "../FinancialPlanContext";

const BimTab = ({
  projectId,
  onAddSpecialist,
  AddUserIcon,
  isEditable = true,
}) => {
  const {
    financialPlanData,
    updateFinancialPlanSection,
    selectedSpecialists,
    removeSpecialist,
    performCalculation,
    isLoading,
    updateSpecialistEfforts,
    getSpecialistEfforts,
  } = useFinancialPlanState();

  const bimSectionName = "BIM координирование";
  const bimData = financialPlanData.sections.bim_coordination;
  const [bonusPercent, setBonusPercent] = useState(bimData.bonus_percent);
  const [bimPercent, setBimPercent] = useState(bimData.bim_percent || 4);
  const [editingFields, setEditingFields] = useState({});
  const [tempValues, setTempValues] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [expandedRoles, setExpandedRoles] = useState(new Set());
  const prevSpecialistsRef = useRef({});

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("ru-RU").format(value);
  };

  // Функция для запуска калькуляции BIM координирования
  // Теперь payload формируется автоматически в контексте из selectedSpecialists
  const triggerBimCalculation = useCallback(async () => {
    try {
      setIsCalculating(true);
      // Запускаем калькуляцию без переопределений - контекст автоматически соберет данные из selectedSpecialists
      await performCalculation(projectId, {});
    } catch (error) {
      console.error("Ошибка калькуляции при изменении специалистов:", error);
    } finally {
      setIsCalculating(false);
    }
  }, [performCalculation, projectId]);

  // Отслеживаем изменения специалистов и запускаем калькуляцию
  useEffect(() => {
    const prevSpecialists = prevSpecialistsRef.current;
    const currentSpecialists = selectedSpecialists || {};

    // Проверяем, изменились ли специалисты для BIM координирования
    const bimSectionName = "BIM координирование";
    const bimSpecialistKeys = Object.keys(currentSpecialists).filter((key) =>
      key.startsWith(`${bimSectionName}-`)
    );

    let hasChanges = false;
    for (const key of bimSpecialistKeys) {
      const prevValue = prevSpecialists[key];
      const currentValue = currentSpecialists[key];

      // Сравниваем массивы специалистов
      if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
        hasChanges = true;
        break;
      }
    }

    // Если есть изменения и это не первая загрузка
    if (hasChanges && Object.keys(prevSpecialists).length > 0) {
      console.log(
        "Обнаружены изменения в специалистах BIM координирования, запускаем калькуляцию"
      );
      triggerBimCalculation();
    }

    // Обновляем ссылку на предыдущее состояние
    prevSpecialistsRef.current = { ...currentSpecialists };
  }, [selectedSpecialists, triggerBimCalculation]);

  // Функции для работы с исполнителями
  const handleSpecialistDelete = (blockName, sectionName, specialistIndex) => {
    removeSpecialist(sectionName, blockName, specialistIndex);
  };

  const getSpecialistKey = (sectionName, roleName) => {
    return `${sectionName}-${roleName}`;
  };

  const toggleRoleExpanded = (roleName) => {
    setExpandedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(roleName)) next.delete(roleName);
      else next.add(roleName);
      return next;
    });
  };

  // Функции для редактирования полей
  const handleStartEdit = (fieldName, currentValue) => {
    setEditingFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
    setTempValues((prev) => ({
      ...prev,
      [fieldName]: currentValue,
    }));
  };

  const handleSaveEdit = async (fieldName, value) => {
    const numValue = Number(value);
    setEditingFields((prev) => ({
      ...prev,
      [fieldName]: false,
    }));

    if (fieldName === "bonus_percent") {
      setBonusPercent(numValue);
      updateFinancialPlanSection("bim_coordination", {
        bonus_percent: numValue,
      });

      // Запускаем калькуляцию при изменении процента бонусов
      try {
        setIsCalculating(true);
        await performCalculation(projectId, {
          bim_coordination: {
            direct_bonus_percent: numValue,
          },
        });
      } catch (error) {
        console.error(
          "Ошибка калькуляции при изменении процента бонусов:",
          error
        );
      } finally {
        setIsCalculating(false);
      }
    } else if (fieldName === "bim_percent") {
      setBimPercent(numValue);
      updateFinancialPlanSection("bim_coordination", { bim_percent: numValue });

      // Запускаем калькуляцию при изменении процента BIM координирования
      try {
        setIsCalculating(true);
        await performCalculation(projectId, {
          bim_coordination: {
            quality_control_percent: numValue,
          },
        });
      } catch (error) {
        console.error("Ошибка калькуляции при изменении процента BIM:", error);
      } finally {
        setIsCalculating(false);
      }
    }
  };

  const handleCancelEdit = (fieldName) => {
    setEditingFields((prev) => ({
      ...prev,
      [fieldName]: false,
    }));
  };

  const handleStartEditNumber = (fieldKey, currentValue) => {
    setEditingFields((prev) => ({ ...prev, [fieldKey]: true }));
    setTempValues((prev) => ({ ...prev, [fieldKey]: currentValue }));
  };

  const handleCancelEditNumber = (fieldKey) => {
    setEditingFields((prev) => ({ ...prev, [fieldKey]: false }));
  };

  const handleTempValueChange = (fieldName, value) => {
    setTempValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // Трудозатраты по специалисту: из контекста или дефолт из роли
  const getSpecialistEffort = (roleName, specIdx, role, totalSpecs) => {
    const stored = getSpecialistEfforts(bimSectionName, roleName, specIdx);
    const n = totalSpecs || 1;
    const defaults = {
      day_efforts: (role?.labor_days ?? 0) / n,
      bonus_day_efforts: (role?.bonus_days ?? 0) / n,
      money_expenditures: Math.round((role?.direct_costs ?? 0) / n),
      bonus_money_expenditures: Math.round((role?.bonus_costs ?? 0) / n),
    };
    if (stored && typeof stored.day_efforts === "number") {
      return { ...defaults, ...stored };
    }
    return defaults;
  };

  const getRoleTotalsFromSpecialists = (roleName, role, specialists) => {
    if (!specialists || specialists.length === 0) {
      return {
        day_efforts: role?.labor_days ?? 0,
        bonus_day_efforts: role?.bonus_days ?? 0,
        money_expenditures: role?.direct_costs ?? 0,
        bonus_money_expenditures: role?.bonus_costs ?? 0,
      };
    }
    return specialists.reduce(
      (acc, _, specIdx) => {
        const e = getSpecialistEffort(roleName, specIdx, role, specialists.length);
        acc.day_efforts += e.day_efforts ?? 0;
        acc.bonus_day_efforts += e.bonus_day_efforts ?? 0;
        acc.money_expenditures += e.money_expenditures ?? 0;
        acc.bonus_money_expenditures += e.bonus_money_expenditures ?? 0;
        return acc;
      },
      { day_efforts: 0, bonus_day_efforts: 0, money_expenditures: 0, bonus_money_expenditures: 0 }
    );
  };

  const handleSaveSpecialistEffort = async (roleName, specIdx, fieldName, value) => {
    const num = parseFloat(value);
    if (fieldName === "day_efforts") {
      updateSpecialistEfforts(bimSectionName, roleName, specIdx, { day_efforts: isNaN(num) ? 0 : num });
    } else if (fieldName === "bonus_day_efforts") {
      updateSpecialistEfforts(bimSectionName, roleName, specIdx, { bonus_day_efforts: isNaN(num) ? 0 : num });
    }
    try {
      setIsCalculating(true);
      await performCalculation(projectId);
    } finally {
      setIsCalculating(false);
    }
  };

  // Обработчик изменения чекбокса бонусов
  const handleBonusCheckboxChange = async (checked) => {
    updateFinancialPlanSection("bim_coordination", {
      include_bonuses: checked,
    });

    // Запускаем калькуляцию при изменении чекбокса
    try {
      setIsCalculating(true);
      await performCalculation(projectId, {
        bim_coordination: {
          include_bonuses: checked,
        },
      });
    } catch (error) {
      console.error(
        "Ошибка калькуляции при изменении чекбокса бонусов:",
        error
      );
    } finally {
      setIsCalculating(false);
    }
  };

  // Компонент для редактируемого поля
  const EditableField = ({
    fieldName,
    value,
    isEditing,
    tempValue,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onTempValueChange,
    isEditable = true,
  }) => {
    const inputRef = useRef(null);
    const [localValue, setLocalValue] = useState(
      value === undefined || value === null ? "" : String(value)
    );
    const originalValueRef = useRef(value);

    // Обновляем локальное значение при изменении props или при входе в режим редактирования
    useEffect(() => {
      if (isEditing) {
        setLocalValue(
          tempValue === undefined || tempValue === null ? "" : String(tempValue)
        );
      } else {
        setLocalValue(
          value === undefined || value === null ? "" : String(value)
        );
      }
    }, [isEditing, value, tempValue]);

    // Сохраняем исходное значение при входе в режим редактирования
    useEffect(() => {
      if (isEditing) {
        originalValueRef.current = value;
      }
    }, [isEditing, value]);

    // Автофокус и выделение текста при входе в режим редактирования
    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    if (isEditing) {
      return (
        <input
          type="number"
          step="0.1"
          min="0"
          max="100"
          value={localValue}
          ref={inputRef}
          onChange={(e) => {
            const raw = e.target.value;
            // Разрешаем только цифры и одну точку/запятую, убираем остальные символы
            const sanitized = raw
              .replace(/,/g, ".")
              .replace(/[^0-9.]/g, "")
              .replace(/(\..*)\./g, "$1");
            setLocalValue(sanitized);
            // Обновляем родительское состояние для синхронизации
            onTempValueChange(fieldName, sanitized);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              onSaveEdit(fieldName, localValue);
            } else if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              setLocalValue(
                value === undefined || value === null ? "" : String(value)
              );
              onCancelEdit(fieldName);
            }
          }}
          onBlur={() => {
            // Проверяем, изменилось ли значение от исходного
            const originalStr = originalValueRef.current === undefined || originalValueRef.current === null ? "" : String(originalValueRef.current);
            const currentStr = localValue;
            
            // Если значение не изменилось — просто отменяем редактирование
            if (originalStr === currentStr) {
              onCancelEdit(fieldName);
              return;
            }
            
            // Если значение пустое или некорректное — восстанавливаем исходное
            const numValue = parseFloat(localValue);
            if (localValue === "" || Number.isNaN(numValue)) {
              setLocalValue(originalStr);
              onCancelEdit(fieldName);
              return;
            }
            
            // Автоматически сохраняем при потере фокуса только если значение валидно и изменилось
            onSaveEdit(fieldName, localValue);
          }}
          disabled={!isEditable || isCalculating || isLoading}
          style={{
            width: "80px",
            padding: "6px 14px",
            border: "1px solid #007bff",
            borderRadius: "7px",
            fontSize: "17px",
            fontWeight: "600",
            color: "#2256a1",
            background: "#FFFFFF",
            textAlign: "center",
            outline: "none",
          }}
        />
      );
    }

    return (
      <div
        className="bim-input"
        style={{
          cursor:
            !isEditable || isCalculating || isLoading
              ? "not-allowed"
              : "pointer",
          border: "1px solid #e0e0e0",
          background: !isEditable ? "#F4F6FA" : "#f5f8fa",
          opacity: !isEditable || isCalculating || isLoading ? 0.6 : 1,
        }}
        onClick={() => {
          if (isEditable && !isCalculating && !isLoading) {
            onStartEdit(fieldName, value);
          }
        }}
      >
        {value}%
      </div>
    );
  };

  // Редактируемое числовое поле для специалистов (трудозатраты, бонусы в днях)
  const EditableNumberField = ({
    roleName,
    specIdx,
    fieldName,
    value,
    isEditing,
    tempValue,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onTempValueChange,
    format = (v) => String(v ?? ""),
    isEditable = true,
  }) => {
    const fieldKey = `bim-${roleName}-${specIdx}-${fieldName}`;
    const inputRef = useRef(null);
    const [localValue, setLocalValue] = useState(
      value === undefined || value === null ? "" : String(value)
    );
    const originalValueRef = useRef(value);

    useEffect(() => {
      if (isEditing) {
        setLocalValue(tempValue === undefined || tempValue === null ? "" : String(tempValue));
      } else {
        setLocalValue(value === undefined || value === null ? "" : String(value));
      }
    }, [isEditing, value, tempValue]);

    // Сохраняем исходное значение при входе в режим редактирования
    useEffect(() => {
      if (isEditing) {
        originalValueRef.current = value;
      }
    }, [isEditing, value]);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    if (isEditing) {
      return (
        <input
          type="number"
          step="0.1"
          min="0"
          value={localValue}
          ref={inputRef}
          onChange={(e) => {
            const raw = e.target.value;
            const sanitized = raw
              .replace(/,/g, ".")
              .replace(/[^0-9.]/g, "")
              .replace(/(\..*)\./g, "$1");
            setLocalValue(sanitized);
            onTempValueChange(fieldKey, sanitized);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSaveEdit(roleName, specIdx, fieldName, localValue);
            } else if (e.key === "Escape") {
              e.preventDefault();
              setLocalValue(value === undefined || value === null ? "" : String(value));
              onCancelEdit(fieldKey);
            }
          }}
          onBlur={() => {
            // Проверяем, изменилось ли значение от исходного
            const originalStr = originalValueRef.current === undefined || originalValueRef.current === null ? "" : String(originalValueRef.current);
            const currentStr = localValue;
            
            // Если значение не изменилось — просто отменяем редактирование
            if (originalStr === currentStr) {
              onCancelEdit(fieldKey);
              return;
            }
            
            // Если значение пустое или некорректное — восстанавливаем исходное
            const numValue = parseFloat(localValue);
            if (localValue === "" || Number.isNaN(numValue)) {
              setLocalValue(originalStr);
              onCancelEdit(fieldKey);
              return;
            }
            
            // Автоматически сохраняем при потере фокуса только если значение валидно и изменилось
            onSaveEdit(roleName, specIdx, fieldName, localValue);
          }}
          disabled={!isEditable || isCalculating || isLoading}
          style={{
            width: "56px",
            padding: "4px 8px",
            border: "1px solid #007bff",
            borderRadius: "4px",
            fontSize: "14px",
            textAlign: "center",
            outline: "none",
            background: "white",
          }}
        />
      );
    }

    return (
      <span
        style={{
          fontSize: "14px",
          display: "inline-block",
          cursor: isEditable ? "pointer" : "default",
          padding: "6px 10px",
          border: "1.5px dashed #B8C2D1",
          borderRadius: "6px",
          background: isEditable ? "#F9FBFF" : "#F4F6FA",
          color: "#1F2937",
          minWidth: "56px",
          textAlign: "center",
        }}
        onClick={() => isEditable && !isCalculating && !isLoading && onStartEdit(fieldKey, value)}
      >
        {format(value)}
      </span>
    );
  };

  const roles = bimData.sections || [];

  return (
    <div className="bim-table-block">
      {/* Чекбокс "Учитывать бонусы при расчете" */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          padding: "16px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <label style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
            Учитывать бонусы при расчете:
          </label>
          <label className="switch">
            <input
              type="checkbox"
              checked={bimData.include_bonuses}
              onChange={(e) => handleBonusCheckboxChange(e.target.checked)}
              disabled={!isEditable || isCalculating || isLoading}
            />
            <span className="slider"></span>
          </label>
          {isCalculating && (
            <span style={{ fontSize: "12px", color: "#007bff" }}>
              ⏳ Выполняется калькуляция...
            </span>
          )}
        </div>
        {/* <button
          onClick={() => {}}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>+</span>
          Добавить раздел
        </button> */}
      </div>

      <div className="bim-title-row">
        <div className="bim-title-block">
          <div className="bim-title">% доля BIM координирования</div>
          <EditableField
            fieldName="bim_percent"
            value={bimPercent}
            isEditing={editingFields.bim_percent}
            tempValue={tempValues.bim_percent}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onTempValueChange={handleTempValueChange}
            isEditable={isEditable}
          />
        </div>
        <div className="bim-title-block">
          <div className="bim-title">% доля бонусов от ПЗ</div>
          <EditableField
            fieldName="bonus_percent"
            value={bonusPercent}
            isEditing={editingFields.bonus_percent}
            tempValue={tempValues.bonus_percent}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onTempValueChange={handleTempValueChange}
            isEditable={isEditable}
          />
        </div>
      </div>

      <div className="bim-table-mock">
        <div className="bim-expanded-hint" style={{ marginBottom: "8px", fontSize: "13px", color: "#666" }}>
          💡 Кликните по числу, чтобы отредактировать. Изменения сохраняются при потере фокуса.
        </div>
        {roles.length > 0 ? (
          <table className="bim-roles-table">
            <thead>
              <tr>
                <th className="bim-th-name"></th>
                <th>Трудозатраты (дн)</th>
                <th>Бонусы в днях (дн)</th>
                <th>Прямые затраты (тг)</th>
                <th>Бонусы в деньгах (тг)</th>
                <th className="bim-th-action"></th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => {
                const specialistKey = getSpecialistKey(bimSectionName, role.name);
                const specialists = selectedSpecialists?.[specialistKey] || [];
                const isExpanded = expandedRoles.has(role.name);
                const totals = getRoleTotalsFromSpecialists(role.name, role, specialists);

                return (
                  <React.Fragment key={role.name}>
                    <tr
                      className="bim-role-row"
                      onClick={() => toggleRoleExpanded(role.name)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="bim-td-name">
                        <span
                          className="section-arrow"
                          style={{
                            transform: isExpanded ? "rotate(90deg)" : "none",
                            display: "inline-block",
                            marginRight: "6px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRoleExpanded(role.name);
                          }}
                        >
                          ▶
                        </span>
                        <span>{role.name}</span>
                        {isExpanded && specialists.length > 0 && (
                          <span style={{ color: "#666", fontWeight: "normal" }}>
                            {" "}({specialists.length}{" "}
                            {specialists.length === 1 ? "исполнитель" : specialists.length < 5 ? "исполнителя" : "исполнителей"})
                          </span>
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>{Number(totals.day_efforts).toFixed(1)}</td>
                      <td onClick={(e) => e.stopPropagation()}>{Number(totals.bonus_day_efforts).toFixed(1)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <span className="tenge-inline">₸</span> {formatCurrency(totals.money_expenditures)}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <span className="tenge-inline">₸</span> {formatCurrency(totals.bonus_money_expenditures)}
                      </td>
                      <td className="bim-td-action"></td>
                    </tr>
                    {isExpanded && (
                      <>
                        {specialists.map((specialist, specIdx) => {
                          const effort = getSpecialistEffort(role.name, specIdx, role, specialists.length);
                          const specLabel =
                            specialist.type === "employee"
                              ? specialist.fullName || specialist.name || `Специалист ${specIdx + 1}`
                              : specialist.fullName || specialist.name || `Субподрядчик ${specIdx + 1}`;
                          const fieldKeyDay = `bim-${role.name}-${specIdx}-day_efforts`;

                          return (
                            <tr
                              key={specIdx}
                              className="bim-specialist-row"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <td className="bim-td-name">
                                {specLabel}
                              </td>
                              <td>
                                <EditableNumberField
                                  roleName={role.name}
                                  specIdx={specIdx}
                                  fieldName="day_efforts"
                                  value={effort.day_efforts ?? 0}
                                  isEditing={editingFields[fieldKeyDay]}
                                  tempValue={tempValues[fieldKeyDay]}
                                  onStartEdit={handleStartEditNumber}
                                  onSaveEdit={(rName, sIdx, fName, val) => {
                                    setEditingFields((prev) => ({ ...prev, [fieldKeyDay]: false }));
                                    handleSaveSpecialistEffort(rName, sIdx, fName, val);
                                  }}
                                  onCancelEdit={handleCancelEditNumber}
                                  onTempValueChange={handleTempValueChange}
                                  format={(v) => (v ?? 0).toFixed(1)}
                                  isEditable={isEditable}
                                />
                              </td>
                              <td>
                                {(effort.bonus_day_efforts ?? 0).toFixed(1)}
                              </td>
                              <td>
                                <span className="tenge-inline">₸</span> {formatCurrency(effort.money_expenditures ?? 0)}
                              </td>
                              <td>
                                <span className="tenge-inline">₸</span> {formatCurrency(effort.bonus_money_expenditures ?? 0)}
                              </td>
                              <td className="bim-td-action">
                                {isEditable && (
                                  <button
                                    type="button"
                                    className="expanded-specialist-delete-btn"
                                    onClick={() =>
                                      handleSpecialistDelete(role.name, bimSectionName, specIdx)
                                    }
                                    title="Удалить"
                                  >
                                    🗑
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="bim-add-row expanded-block-add-row" onClick={(e) => e.stopPropagation()}>
                          <td colSpan={6} className="expanded-block-performers-cell">
                            <div className="expanded-block-performers">
                              {isEditable && onAddSpecialist && (
                                <button
                                  type="button"
                                  className="add-performer-btn"
                                  onClick={() => onAddSpecialist(role.name, bimSectionName)}
                                  disabled={!isEditable || isCalculating || isLoading}
                                >
                                  <span className="add-performer-btn-icon">+</span>
                                  Добавить исполнителя
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ textAlign: "center", padding: "40px", color: "#666", fontSize: "16px" }}>
            Загрузка данных...
          </div>
        )}
      </div>

      <div className="bim-table-footer">
        <div className="bim-footer-main">
          <div className="bim-footer-label">ВСЕ ЗАТРАТЫ</div>
          <div className="bim-footer-sum main">
            ₸ {formatCurrency(bimData.total_costs)}
          </div>
        </div>
        <div className="bim-footer-right">
          <div className="bim-footer-block">
            <div className="bim-footer-label">Прямые затраты</div>
            <div className="bim-footer-sum">
              ₸ {formatCurrency(bimData.direct_costs)}
            </div>
          </div>
          <div className="bim-footer-block">
            <div className="bim-footer-label">Бонусы</div>
            <div className="bim-footer-sum">
              ₸ {formatCurrency(bimData.bonus_fund)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BimTab;
